import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, session as readSession } from '../../shell/bridge';
import { formatApiError } from '../../lib/errorCopy';
import { listDecisions, voteDecision } from '../../lib/api';
import { DECISION_VERBS, selectDecisionRail, type DecisionView } from '../../../../shared/decisions';
import type { DateView } from '../../../../shared/types/dates';
import type { ChoreItem } from './Chores/types';
import { dueLabel } from './Chores/helpers';
import { Tooltip } from '../../ui/Tooltip';
import { SkeletonBlock } from '../../ui/Skeleton';

/**
 * Life → Today — the consolidated dashboard and the landing tab: one
 * time-ordered view over everything that needs the couple or is coming up,
 * merged client-side from the per-type endpoints (asks, decisions, household
 * items, weekly-sync tasks, "to talk about" shares, dates). Every row is one
 * line with its kind chip; the primary action runs inline where the owning
 * surface already supports it (tick off a household item, pick a decision),
 * and everything else deep-links to its owning sub-tab — the dashboard
 * consolidates the VIEW, never the data, so there is still one source of
 * truth per type.
 */

interface ReqRow {
  requestId: string;
  fromMemberId: string;
  toMemberId: string | null;
  text: string;
  state: 'posted' | 'countered' | 'accepted' | 'declined' | 'snoozed' | 'done';
  lane: 'life' | 'us';
  snoozedUntil: number | null;
}

interface PlanTaskRow {
  taskId: string;
  title: string;
  assignee: 'me' | 'partner' | 'anyone';
  state: 'open' | 'done';
  createdBy: string;
}

interface ShareRow {
  shareId: string;
  text: string;
  talk: boolean;
  audience: 'shared' | 'secret';
}

type Kind = 'ask' | 'decision' | 'household' | 'date' | 'sync' | 'talk';

const KIND_LABEL: Record<Kind, string> = {
  ask: 'Ask',
  decision: 'Decision',
  household: 'Household',
  date: 'Date',
  sync: 'Weekly sync',
  talk: 'To talk about',
};

const TODAY_OVERVIEW =
  'Everything that needs you or is coming up, in one list: asks waiting on a reply, ' +
  'decisions waiting on a pick, household items due or overdue, tasks filed by the weekly ' +
  'sync, and dates on the horizon. Tap a row\u2019s action to do it here, or open the ' +
  'section it belongs to for the full list.';

const fmtWhen = (ms: number | null): string => {
  if (ms == null) return 'No date yet';
  const at = new Date(ms);
  const day = at.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  const hasTime = at.getHours() !== 12 || at.getMinutes() !== 0;
  return hasTime ? `${day} · ${at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : day;
};

export function Today({ onOpen }: { onOpen: (key: string) => void }) {
  const me = readSession();
  const [chores, setChores] = useState<ChoreItem[] | null>(null);
  const [requests, setRequests] = useState<ReqRow[] | null>(null);
  const [decisions, setDecisions] = useState<DecisionView[] | null>(null);
  const [tasks, setTasks] = useState<PlanTaskRow[] | null>(null);
  const [shares, setShares] = useState<ShareRow[] | null>(null);
  const [dates, setDates] = useState<DateView[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState('');

  // One endpoint failing must not blank the whole dashboard (review 2026-11-04):
  // allSettled keeps every source that answered, and a source that did not is
  // reported under its own name. A single Promise.all would reject on the first
  // fault and leave the page empty even though five of six requests succeeded.
  const refresh = useCallback(async () => {
    const [c, r, d, t, s, dt] = await Promise.allSettled([
      api<{ chores: ChoreItem[] }>('/api/chores'),
      api<{ requests: ReqRow[] }>('/api/requests?lane=life'),
      listDecisions(),
      api<{ tasks: PlanTaskRow[] }>('/api/plan/tasks'),
      api<{ shares: ShareRow[] }>('/api/plan/shares'),
      api<{ ideas: DateView[] }>('/api/dates'),
    ]);
    const failed: string[] = [];
    if (c.status === 'fulfilled') setChores(c.value.chores ?? []);
    else failed.push('household');
    if (r.status === 'fulfilled') setRequests(r.value.requests ?? []);
    else failed.push('requests');
    if (d.status === 'fulfilled') setDecisions((d.value.decisions ?? []) as DecisionView[]);
    else failed.push('decisions');
    if (t.status === 'fulfilled') setTasks(t.value.tasks ?? []);
    else failed.push('weekly sync');
    if (s.status === 'fulfilled') setShares(s.value.shares ?? []);
    else failed.push('to talk about');
    if (dt.status === 'fulfilled') setDates(dt.value.ideas ?? []);
    else failed.push('dates');
    // Say which part is missing rather than claiming the whole list is gone.
    const all = failed.length === 6;
    setError(
      all
        ? 'Could not load today\u2019s list.'
        : failed.length
          ? `Could not load ${failed.join(', ')}. The rest of today\u2019s list is below.`
          : '',
    );
  }, []);

  useEffect(() => {
    void refresh();
    const reload = () => { void refresh(); };
    window.addEventListener('pear:chores', reload);
    window.addEventListener('pear:decisions', reload);
    window.addEventListener('pear:requests', reload);
    window.addEventListener('focus', reload);
    return () => {
      window.removeEventListener('pear:chores', reload);
      window.removeEventListener('pear:decisions', reload);
      window.removeEventListener('pear:requests', reload);
      window.removeEventListener('focus', reload);
    };
  }, [refresh]);

  const tickOff = async (c: ChoreItem) => {
    if (busyId) return;
    setBusyId(c.choreId);
    setError('');
    try {
      await api(`/api/chores/${c.choreId}/complete`, { method: 'POST', body: {} });
      await refresh();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const pick = async (id: string, choice: 'a' | 'b') => {
    if (busyId) return;
    setBusyId(id);
    setError('');
    try {
      await voteDecision(id, choice);
      await refresh();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const { needsYou, comingUp } = useMemo(() => {
    const now = Date.now();
    const openChores = (chores ?? []).filter(c => c.status === 'open');
    const overdue = openChores
      .filter(c => c.dueAt != null && c.dueAt < now)
      .sort((a, b) => (a.dueAt ?? 0) - (b.dueAt ?? 0));
    const upcomingChores = openChores
      .filter(c => c.dueAt == null || c.dueAt >= now)
      .sort((a, b) => (a.dueAt ?? Infinity) - (b.dueAt ?? Infinity))
      .slice(0, 8);
    const asks = (requests ?? []).filter(
      r => (r.state === 'posted' || r.state === 'countered') && (r.toMemberId == null || r.toMemberId === me?.memberId),
    );
    const openDecisions = decisions ? selectDecisionRail(decisions).open : [];
    const openTasks = (tasks ?? []).filter(t => t.state === 'open');
    const agenda = (shares ?? []).filter(s => s.talk === true && s.audience === 'shared');
    const upcomingDates = (dates ?? [])
      .filter(d => d.when != null && (d.state === 'suggested' || d.state === 'locked'))
      .sort((a, b) => (a.when ?? 0) - (b.when ?? 0))
      .slice(0, 6);
    return {
      needsYou: { asks, openDecisions, overdue },
      comingUp: { upcomingChores, upcomingDates, openTasks, agenda },
    };
  }, [chores, requests, decisions, tasks, shares, dates, me?.memberId]);

  const needsCount = needsYou.asks.length + needsYou.openDecisions.length + needsYou.overdue.length;

  return (
    <section className="td-panel" id="life-today-panel" aria-labelledby="life-today-title">
      <h2
        id="life-today-title"
        className="ch-title"
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'var(--type-h3-size)',
          letterSpacing: 'var(--type-tracking-wider)',
          fontWeight: 400,
          color: 'var(--color-cream)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
        }}
      >
        Today
        <Tooltip label={TODAY_OVERVIEW} tipFor="life-today" />
      </h2>

      {chores === null && requests === null && decisions === null && (
        <SkeletonBlock lines={4} />
      )}

      {error && <p className="plan-error" role="alert">{error}</p>}

      {/* ── needs you ─────────────────────────────────────────── */}
      <section className="td-section" data-test="today-needs-you" aria-label="Needs you">
        <h3 className="plan-section-title">
          Needs you{needsCount > 0 ? ` (${needsCount})` : ''}
        </h3>
        {needsCount === 0 && chores !== null && (
          <p className="rq-empty-hint" data-test="today-needs-empty">
            Nothing needs you right now — nice.
          </p>
        )}

        {needsYou.overdue.map(c => (
          <div className="td-row" key={'ch-' + c.choreId} data-test="today-row" data-kind="household">
            <span className="life-kind-chip life-kind-household">Household</span>
            <span className="td-text">{c.title}</span>
            <span className="td-meta">Overdue · {dueLabel(c)}</span>
            <button
              type="button"
              className="pear-btn pear-btn-mustard td-action"
              data-test="today-tick"
              disabled={busyId === c.choreId}
              onClick={() => void tickOff(c)}
            >
              {busyId === c.choreId ? '…' : 'Tick off'}
            </button>
          </div>
        ))}

        {needsYou.asks.map(r => (
          <div className="td-row" key={'ask-' + r.requestId} data-test="today-row" data-kind="ask">
            <span className="life-kind-chip life-kind-ask">Ask</span>
            <span className="td-text">{r.text}</span>
            <span className="td-meta">{r.state === 'countered' ? 'Countered — reply' : 'Waiting on a reply'}</span>
            <button
              type="button"
              className="pear-btn td-action"
              data-test="today-open-asks"
              onClick={() => onOpen('requests')}
            >
              Reply
            </button>
          </div>
        ))}

        {needsYou.openDecisions.map(v => (
          <div className="td-row td-row-decision" key={'dec-' + v.decisionId} data-test="today-row" data-kind="decision">
            <span className="life-kind-chip life-kind-decision">Decision</span>
            <span className="td-text">
              {v.title} — <b>{v.options.a}</b> or <b>{v.options.b}</b>
            </span>
            <span className="td-actions">
              <button
                type="button"
                className="pear-btn pear-btn-mustard td-action"
                data-test="today-decision-a"
                disabled={busyId === v.decisionId}
                onClick={() => void pick(v.decisionId, 'a')}
              >
                {DECISION_VERBS.a}
              </button>
              <button
                type="button"
                className="pear-btn td-action"
                data-test="today-decision-b"
                disabled={busyId === v.decisionId}
                onClick={() => void pick(v.decisionId, 'b')}
              >
                {DECISION_VERBS.b}
              </button>
            </span>
          </div>
        ))}
      </section>

      {/* ── coming up ─────────────────────────────────────────── */}
      <section className="td-section" data-test="today-coming-up" aria-label="Coming up">
        <h3 className="plan-section-title">Coming up</h3>
        {chores !== null &&
          comingUp.upcomingChores.length === 0 &&
          comingUp.upcomingDates.length === 0 &&
          comingUp.openTasks.length === 0 &&
          comingUp.agenda.length === 0 && (
            <p className="rq-empty-hint" data-test="today-upcoming-empty">
              Nothing coming up — add something to the Household list or Plan a week.
            </p>
          )}

        {comingUp.upcomingChores.map(c => (
          <div className="td-row" key={'ch-' + c.choreId} data-test="today-row" data-kind="household">
            <span className="life-kind-chip life-kind-household">Household</span>
            <span className="td-text">{c.title}</span>
            <span className="td-meta">{dueLabel(c)}</span>
            <button
              type="button"
              className="pear-btn pear-btn-mustard td-action"
              data-test="today-tick"
              disabled={busyId === c.choreId}
              onClick={() => void tickOff(c)}
            >
              {busyId === c.choreId ? '…' : 'Tick off'}
            </button>
          </div>
        ))}

        {comingUp.upcomingDates.map(d => (
          <div className="td-row" key={'date-' + d.ideaId} data-test="today-row" data-kind="date">
            <span className="life-kind-chip life-kind-date">Date</span>
            <span className="td-text">{d.title}</span>
            <span className="td-meta">{fmtWhen(d.when)} · {d.state}</span>
            <button
              type="button"
              className="pear-btn td-action"
              data-test="today-open-dates"
              onClick={() => { window.location.hash = '#us-dates'; }}
            >
              Dates
            </button>
          </div>
        ))}

        {comingUp.openTasks.map(t => (
          <div className="td-row" key={'task-' + t.taskId} data-test="today-row" data-kind="sync">
            <span className="life-kind-chip life-kind-sync">Weekly sync</span>
            <span className="td-text">{t.title}</span>
            <span className="td-meta">Filed by the weekly sync</span>
            <button
              type="button"
              className="pear-btn td-action"
              data-test="today-open-plan"
              onClick={() => onOpen('plan')}
            >
              Plan
            </button>
          </div>
        ))}

        {comingUp.agenda.map(s => (
          <div className="td-row" key={'share-' + s.shareId} data-test="today-row" data-kind="talk">
            <span className="life-kind-chip life-kind-talk">To talk about</span>
            <span className="td-text">{s.text}</span>
            <button
              type="button"
              className="pear-btn td-action"
              data-test="today-open-plan"
              onClick={() => onOpen('plan')}
            >
              Plan
            </button>
          </div>
        ))}
      </section>
    </section>
  );
}
